import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CreditCard, ExternalLink, RefreshCw, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import PageHeader from '../components/shared/PageHeader';
import PlanBrand, { isSynesPlan } from '../components/shared/PlanBrand';
import Select from '../components/ui/Select';
import Table from '../components/ui/Table';
import { supabase, supabaseAnonKey, supabaseUrl } from '../lib/supabase';
import { currency, date } from '../utils/formatters';

const emptySubscription = {
  id: null,
  plano: 'Não informado',
  valor_mensal: 0,
  data_vencimento: null,
  status_pagamento: 'pendente',
};

const prepayOptions = [
  { months: 1, discount: 0, label: 'Mensal' },
  { months: 3, discount: 5, label: '3 meses' },
  { months: 6, discount: 10, label: '6 meses' },
  { months: 12, discount: 15, label: '12 meses' },
];

const gdnProfiles = new Set(['super_admin', 'gdn_admin']);

function formatDate(value) {
  if (!value) return 'Não informado';
  return date(String(value).split('T')[0]);
}

function getDaysToDue(value) {
  if (!value) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${String(value).split('T')[0]}T00:00:00`);
  return Math.ceil((due.getTime() - today.getTime()) / 86400000);
}

function billingCurrency(value, moeda = 'BRL') {
  const currencyCode = String(moeda || 'BRL').toUpperCase();
  return currency(value, {
    currency: currencyCode,
    locale: currencyCode === 'BRL' ? 'pt-BR' : 'en-US',
  });
}

async function withTimeout(promise, message, timeoutMs = 20000) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function invokePaymentFunction(name, body) {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;

  if (!accessToken) {
    throw new Error('Sessão expirada. Saia do sistema, entre novamente e tente outra vez.');
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: {
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body || {}),
  });

  const text = await response.text();
  let payload = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }

  if (!response.ok) {
    const message = typeof payload === 'string'
      ? payload
      : payload?.message || payload?.error || JSON.stringify(payload || {});
    throw new Error(message || 'Não foi possível concluir esta ação agora.');
  }

  return payload;
}

export default function Pagamento() {
  const location = useLocation();
  const [subscription, setSubscription] = useState(emptySubscription);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentFeedback, setPaymentFeedback] = useState(null);
  const [lastPaymentUrl, setLastPaymentUrl] = useState('');
  const [loadError, setLoadError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(3);
  const [isGdnAdmin, setIsGdnAdmin] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState('');

  const daysToDue = useMemo(() => getDaysToDue(subscription.data_vencimento), [subscription.data_vencimento]);
  const status = String(subscription.status_pagamento || '').toLowerCase();
  const isLate = daysToDue !== null && daysToDue < 0;
  const isNearDue = daysToDue !== null && daysToDue >= 0 && daysToDue <= 7;
  const hasPendingPlan = Boolean(subscription.pending_plan_id || subscription.pending_plan_code);
  const billingPlanName = subscription.pending_plan_name || subscription.plano;
  const billingAmount = Number(subscription.pending_plan_value || subscription.valor_mensal || 0);
  const canPay = Boolean(subscription.id) && billingAmount > 0;
  const paymentAvailable = canPay && (hasPendingPlan || (daysToDue !== null && daysToDue <= 7));
  const selectedPrepay = prepayOptions.find((option) => option.months === selectedPeriod) || prepayOptions[0];
  const periodSubtotal = billingAmount * selectedPrepay.months;
  const periodTotal = periodSubtotal * (1 - selectedPrepay.discount / 100);
  const periodSavings = periodSubtotal - periodTotal;
  const premiumSynes = isSynesPlan(billingPlanName);
  const subscriptionCurrency = String(subscription.moeda || 'BRL').toUpperCase();
  const paymentProvider = subscription.provedor_pagamento === 'stripe' || subscriptionCurrency !== 'BRL'
    ? 'stripe'
    : 'mercado_pago';
  const paymentProviderLabel = paymentProvider === 'stripe' ? 'Stripe' : 'Mercado Pago';

  useEffect(() => {
    loadPaymentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isGdnAdmin || !selectedSubscriptionId) return;
    loadPaymentData(selectedSubscriptionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubscriptionId, isGdnAdmin]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('status');
    if (!paymentStatus) return;

    const messages = {
      success: 'Pagamento aprovado ou em processamento. Vamos confirmar automaticamente.',
      pending: 'Pagamento pendente. Assim que o Mercado Pago confirmar, a assinatura será atualizada.',
      failure: 'Pagamento não concluído. Você pode tentar novamente quando quiser.',
    };

    setPaymentFeedback({
      type: paymentStatus === 'failure' ? 'error' : 'success',
      message: messages[paymentStatus] || 'Retorno do Mercado Pago recebido.',
    });
    loadPaymentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  function subscriptionLabel(item) {
    const clientName = item.mecsync_clientes?.nome_oficina
      || item.mecsync_clientes?.responsavel
      || item.oficina_id
      || item.id;
    const planName = item.pending_plan_name || item.plano || 'Plano';
    return `${clientName} - ${planName} - ${billingCurrency(item.pending_plan_value || item.valor_mensal || 0, item.moeda)}`;
  }

  async function loadSubscriptionPaymentData(assinatura) {
    setSubscription({
      ...emptySubscription,
      ...assinatura,
    });

    const { data: pagamentos } = await supabase
      .from('mecsync_pagamentos')
      .select('*')
      .eq('assinatura_id', assinatura.id)
      .order('criado_em', { ascending: false });

    setHistory(pagamentos || []);
  }

  async function loadPaymentData(targetSubscriptionId = selectedSubscriptionId) {
    try {
      setLoading(true);
      setLoadError('');
      const { data: authData } = await supabase.auth.getUser();
      const email = authData?.user?.email;
      if (!email) {
        setLoadError('Usuário não autenticado. Faça login novamente.');
        return;
      }

      const { data: userRow } = await supabase
        .from('usuarios')
        .select('oficina_id,perfil')
        .eq('email', email)
        .maybeSingle();

      const userIsGdnAdmin = gdnProfiles.has(String(userRow?.perfil || ''));
      setIsGdnAdmin(userIsGdnAdmin);

      if (userIsGdnAdmin) {
        const { data: allSubscriptions, error: subscriptionsError } = await supabase
          .from('assinaturas')
          .select('*, mecsync_clientes(nome_oficina, responsavel, email, telefone)')
          .order('data_vencimento', { ascending: true });

        if (subscriptionsError) throw subscriptionsError;

        const activeSubscriptions = allSubscriptions || [];
        setSubscriptions(activeSubscriptions);

        const selectedId = targetSubscriptionId || activeSubscriptions[0]?.id || '';
        if (selectedId && selectedId !== selectedSubscriptionId) {
          setSelectedSubscriptionId(selectedId);
        }

        const assinatura = activeSubscriptions.find((item) => item.id === selectedId) || activeSubscriptions[0];
        if (assinatura) {
          await loadSubscriptionPaymentData(assinatura);
        } else {
          setSubscription(emptySubscription);
          setHistory([]);
          setLoadError('Nenhuma assinatura encontrada.');
        }
        return;
      }

      const oficinaId = userRow?.oficina_id;
      if (!oficinaId) {
        setLoadError(`Não encontrei oficina vinculada ao usuário ${email}.`);
        return;
      }

      const { data: assinatura } = await supabase
        .from('assinaturas')
        .select('*')
        .eq('oficina_id', oficinaId)
        .maybeSingle();

      if (assinatura) {
        await loadSubscriptionPaymentData(assinatura);
      } else {
        setSubscription(emptySubscription);
        setHistory([]);
        setLoadError('Nenhuma assinatura encontrada para esta oficina.');
      }
    } catch (error) {
      console.error('Erro ao carregar pagamento:', error);
      toast.error('Não foi possível carregar os dados de pagamento');
    } finally {
      setLoading(false);
    }
  }

  const createPayment = async () => {
    if (!subscription.id) {
      toast.error('Assinatura não encontrada para esta oficina');
      return;
    }

    if (selectedPeriod === 1 && !paymentAvailable) {
      toast.error('O mensal fica disponível nos 7 dias antes do vencimento. Para pagar agora, escolha 3, 6 ou 12 meses.');
      return;
    }

    setPaying(true);
    setPaymentFeedback(null);
    setLastPaymentUrl('');
    try {
      const data = await withTimeout(
        invokePaymentFunction(
          paymentProvider === 'stripe' ? 'create-stripe-checkout-session' : 'create-mercado-pago-preference',
          { assinatura_id: subscription.id, periodo_meses: selectedPeriod },
        ),
        'O pagamento demorou demais para responder. Tente novamente em instantes.',
      );

      if (data?.error) {
        throw new Error(data.details?.message || data.error);
      }

      const paymentUrl = data?.init_point || data?.sandbox_init_point;

      if (!paymentUrl) throw new Error(data?.error || 'Link de pagamento não retornado');

      setLastPaymentUrl(paymentUrl);
      setPaymentFeedback({ type: 'success', message: `Link de pagamento gerado. Abrindo ${paymentProviderLabel}...` });
      window.location.assign(paymentUrl);
    } catch (error) {
      console.error(`Erro ao gerar pagamento ${paymentProviderLabel}:`, error);
      const message = error.message || error.details?.message || `Erro ao gerar link do ${paymentProviderLabel}`;
      setPaymentFeedback({ type: 'error', message });
      toast.error(message);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Pagamento" subtitle={`Mensalidade MecSync via ${paymentProviderLabel}`} />

      {isGdnAdmin && (
        <Card>
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <div>
              <p className="mb-2 text-sm text-zinc-400">Conta do cliente</p>
              <Select
                value={selectedSubscriptionId}
                onChange={(event) => setSelectedSubscriptionId(event.target.value)}
                options={subscriptions.map((item) => ({
                  value: item.id,
                  label: subscriptionLabel(item),
                }))}
              />
            </div>
            <Button variant="ghost" onClick={() => loadPaymentData(selectedSubscriptionId)} disabled={loading}>
              <RefreshCw size={16} />
              Atualizar
            </Button>
          </div>
        </Card>
      )}

      {loadError && (
        <Card className="border-red-500/40 bg-red-500/10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-400" size={20} />
              <div>
                <h3 className="font-semibold">Pagamento indisponível</h3>
                <p className="mt-1 text-sm text-zinc-300">{loadError}</p>
              </div>
            </div>
            <Button variant="ghost" onClick={() => loadPaymentData()} disabled={loading}>
              <RefreshCw size={16} />
              Atualizar
            </Button>
          </div>
        </Card>
      )}

      {(hasPendingPlan || isNearDue || isLate || status === 'pendente' || status === 'atrasado' || status === 'trial' || status === 'bloqueado') && (
        <Card className={isLate || status === 'atrasado' ? 'border-red-500/40 bg-red-500/10' : 'border-orange-500/40 bg-orange-500/10'}>
          <div className="flex items-start gap-3">
            <AlertTriangle className={isLate || status === 'atrasado' ? 'text-red-400' : 'text-orange-400'} size={20} />
            <div>
              <h3 className="font-semibold">
                {hasPendingPlan
                  ? 'Plano escolhido para pagamento'
                  : status === 'trial'
                  ? 'Teste grátis em andamento'
                  : status === 'bloqueado'
                    ? 'Conta bloqueada'
                    : isLate || status === 'atrasado' ? 'Mensalidade atrasada' : 'Mensalidade próxima do vencimento'}
              </h3>
              <p className="mt-1 text-sm text-zinc-300">
                {hasPendingPlan
                  ? `Você selecionou o plano ${billingPlanName}. Ao pagar, ele será ativado automaticamente na sua oficina.`
                  : status === 'trial'
                  ? 'Escolha um período com desconto para continuar usando o MecSync sem interrupção quando o teste terminar.'
                  : status === 'bloqueado'
                    ? 'Regularize o pagamento para liberar novamente o acesso ao sistema.'
                    : isLate
                      ? `O vencimento foi em ${formatDate(subscription.data_vencimento)}. Regularize para manter o MecSync ativo.`
                      : `Sua mensalidade vence em ${daysToDue ?? 0} dia${daysToDue === 1 ? '' : 's'}. Você pode pagar agora pela plataforma.`}
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-3 lg:grid-cols-4">
        <Card>
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/15 text-orange-400">
            <Wallet size={20} />
          </div>
          <p className="text-sm text-zinc-400">Plano atual</p>
          {loading ? (
            <p className="mt-1 font-display text-3xl">Carregando...</p>
          ) : premiumSynes ? (
            <PlanBrand plan={billingPlanName} compact className="mt-2" />
          ) : (
            <p className="mt-1 font-display text-3xl">{billingPlanName}</p>
          )}
          {hasPendingPlan && (
            <p className="mt-2 text-xs text-blue-300">Aguardando pagamento para ativar</p>
          )}
        </Card>
        <Card>
          <p className="text-sm text-zinc-400">Valor mensal</p>
          <p className="mt-1 font-display text-3xl">{loading ? 'Carregando...' : billingCurrency(billingAmount, subscriptionCurrency)}</p>
        </Card>
        <Card>
          <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/15 text-blue-400">
            <CreditCard size={20} />
          </div>
          <p className="text-sm text-zinc-400">Data de vencimento</p>
          <p className="mt-1 font-display text-3xl">{loading ? 'Carregando...' : formatDate(subscription.data_vencimento)}</p>
        </Card>
        <Card>
          <p className="mb-3 text-sm text-zinc-400">Status do pagamento</p>
          <Badge tone={status === 'ativo' ? 'finalizada' : 'atrasada'}>
            {loading ? 'Carregando...' : subscription.status_pagamento}
          </Badge>
        </Card>
      </div>

      {!loading && premiumSynes && (
        <Card className="border-yellow-400/30 bg-yellow-400/10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <PlanBrand plan={billingPlanName} compact />
              <h3 className="mt-3 font-semibold text-yellow-100">Entrega Premium com Synes.ia</h3>
              <p className="mt-1 max-w-3xl text-sm leading-6 text-zinc-300">
                Seu plano inclui WhatsApp automatizado, CRM completo, triagem de leads com IA e réguas de relacionamento. A implantação dessa camada comercial é feita pela Synes.ia dentro da experiência MecSync.
              </p>
            </div>
            <Button variant="ghost" onClick={() => window.open('mailto:contato@gdncorp.com.br?subject=Premium%20IA%20MecSync', '_blank', 'noopener,noreferrer')}>
              Falar sobre Premium IA
            </Button>
          </div>
        </Card>
      )}

      <Card>
        <div className="mb-5">
          <h3 className="font-semibold">Pague adiantado e economize</h3>
          <p className="mt-1 text-sm text-zinc-400">
            Quanto maior o período contratado, maior o desconto aplicado automaticamente.
          </p>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            {prepayOptions.map((option) => {
              const subtotal = billingAmount * option.months;
              const total = subtotal * (1 - option.discount / 100);
              const selected = selectedPeriod === option.months;
              return (
                <button
                  key={option.months}
                  type="button"
                  onClick={() => setSelectedPeriod(option.months)}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    selected ? 'border-orange-500 bg-orange-500/10' : 'border-border bg-surface2 hover:border-orange-500/40'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{option.label}</span>
                    {option.discount > 0 && <Badge>{option.discount}% off</Badge>}
                  </div>
                  <p className="mt-2 text-xl font-bold">{billingCurrency(total, subscriptionCurrency)}</p>
                  <p className="text-xs text-zinc-500">
                    {option.months === 1 ? 'Sem desconto' : `Economia de ${billingCurrency(subtotal - total, subscriptionCurrency)}`}
                  </p>
                </button>
              );
            })}
          </div>
          <div className="mt-4 rounded-lg bg-green-500/10 p-3 text-sm text-green-300">
            Selecionado: {selectedPrepay.label} por {billingCurrency(periodTotal, subscriptionCurrency)}
            {periodSavings > 0 ? `, economizando ${billingCurrency(periodSavings, subscriptionCurrency)}.` : '.'}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Pagar dentro da plataforma</h3>
            <p className="mt-1 text-sm text-zinc-400">
              {hasPendingPlan
                ? 'Como há um plano escolhido, o pagamento mensal já está liberado para ativação imediata.'
                : 'O mensal fica liberado perto do vencimento. Pagamentos antecipados podem ser feitos a qualquer momento.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={createPayment} disabled={paying || loading || (selectedPeriod === 1 && !paymentAvailable)}>
              <CreditCard size={16} />
              {paying ? 'Gerando...' : selectedPeriod > 1 || paymentAvailable ? `Pagar com ${paymentProviderLabel}` : 'Pagamento indisponível'}
            </Button>
            <Button variant="ghost" onClick={() => window.open('mailto:financeiro@gdncorp.com.br?subject=Pagamento%20MecSync', '_blank', 'noopener,noreferrer')}>
              Falar com GDN Corp
            </Button>
          </div>
        </div>
        {paymentFeedback && (
          <div className={`mt-4 rounded-lg p-3 text-sm ${
            paymentFeedback.type === 'success'
              ? 'bg-green-500/10 text-green-300'
              : 'bg-red-500/10 text-red-300'
          }`}>
            {paymentFeedback.message}
            {lastPaymentUrl && (
              <button
                type="button"
                className="ml-2 font-semibold underline"
                onClick={() => window.open(lastPaymentUrl, '_blank', 'noopener,noreferrer')}
              >
                Abrir link manualmente
              </button>
            )}
          </div>
        )}
        {!loading && subscription.id && billingAmount <= 0 && (
          <div className="mt-4 rounded-lg bg-orange-500/10 p-3 text-sm text-orange-300">
            Esta assinatura ainda esta sem valor mensal. Ajuste o cliente no Painel GDN antes de gerar o checkout.
          </div>
        )}
      </Card>

      {/*
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Status da integração Mercado Pago</h3>
            <p className="mt-1 text-sm text-zinc-400">
              Verifica se a conexao de pagamento esta pronta para uso.
            </p>
            {gatewayStatus && (
              <div className={`mt-3 rounded-lg p-3 text-sm ${gatewayStatus.ok ? 'bg-green-500/10 text-green-300' : 'bg-red-500/10 text-red-300'}`}>
                {gatewayStatus.ok ? (
                  <span>
                    Conectado em modo {gatewayStatus.environment}. Conta #{gatewayStatus.account?.id || '-'}.
                  </span>
                ) : (
                  <span>{gatewayStatus.message}</span>
                )}
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={testMercadoPagoConnection} disabled={testingGateway}>
            <RefreshCw size={16} />
            {testingGateway ? 'Testando...' : 'Testar conexão'}
          </Button>
        </div>
      */}

      <Card>
        <h3 className="mb-3 font-semibold">Histórico de pagamentos</h3>
        <Table
          columns={['Data', 'Descrição', 'Valor', 'Status', 'Link']}
          rows={history}
          emptyMessage="Nenhum pagamento registrado"
          renderRow={(item) => (
            <tr key={item.id} className="border-t border-border hover:bg-surface2">
              <td className="px-3 py-2">{formatDate(item.data_pagamento || item.criado_em)}</td>
              <td className="px-3 py-2">{item.descricao || 'Mensalidade MecSync'}</td>
              <td className="px-3 py-2">{billingCurrency(item.valor || 0, item.moeda || subscriptionCurrency)}</td>
              <td className="px-3 py-2"><Badge>{item.status || 'pendente'}</Badge></td>
              <td className="px-3 py-2">
                {item.checkout_url || item.mercado_pago_init_point ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300"
                    onClick={() => window.open(item.checkout_url || item.mercado_pago_init_point, '_blank', 'noopener,noreferrer')}
                  >
                    Abrir <ExternalLink size={13} />
                  </button>
                ) : '-'}
              </td>
            </tr>
          )}
        />
      </Card>
    </div>
  );
}
